import { useState } from 'react'

import {
    ResolutionFormError,
    resolutionErrorText,
} from './einvoice-resolution-error'
import type { EInvoice, EInvoiceResolutionSource } from '@/lib/api/einvoice-api'
import { Button } from '@/components/ui/button'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import { useReopenEInvoice } from '@/hooks/use-einvoice-resolution-mutations'
import { useWorkflowTracking } from '@/hooks/use-workflow-tracking'
import { vnCalendarDate } from '@/lib/einvoice-pending-age'
import { m } from '@/lib/paraglide/messages'
import {
    SKIP_REASONS,
    SKIP_REASON_LABELS,
} from '@/lib/schemas/einvoice-resolution-schemas'

interface Props {
    orderId: string
    invoice: EInvoice
    canReopen: boolean
    source: EInvoiceResolutionSource
}

/** "dd/MM" of an instant in Vietnam. */
const dayMonth = (instant: string) =>
    vnCalendarDate(instant).split('-').reverse().slice(0, 2).join('/')

/** The one sentence the card shows: what was decided, by whom and when. */
function closureText(invoice: EInvoice): string {
    const closure = invoice.closure
    const name = closure?.actor_full_name || closure?.actor_username || ''
    if (invoice.status === 'manual_issued') {
        const values = {
            series: invoice.inv_series ?? '',
            number: invoice.inv_no ?? '',
            date: invoice.issued_at ? dayMonth(invoice.issued_at) : '',
        }
        return name
            ? m.einvoice_manual_issued_card_by({ ...values, name })
            : m.einvoice_manual_issued_card(values)
    }
    if (!closure || !name) return m.einvoice_skipped_card()
    const known = SKIP_REASONS.find((reason) => reason === closure.reason)
    const reason =
        closure.reason === 'other' && closure.note
            ? closure.note
            : known
              ? SKIP_REASON_LABELS[known]()
              : closure.note
    return m.einvoice_skipped_card_by({
        reason,
        name,
        date: dayMonth(closure.at),
    })
}

/** Grey card for an order a person closed by hand, with a way back. */
export function EInvoiceResolvedCard({
    orderId,
    invoice,
    canReopen,
    source,
}: Props) {
    const [confirming, setConfirming] = useState(false)
    const text = closureText(invoice)
    return (
        <div className="flex flex-wrap items-center justify-between gap-2 rounded-md border bg-muted/40 p-3">
            <p className="text-sm">{text}</p>
            {canReopen ? (
                <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => setConfirming(true)}
                >
                    {m.einvoice_reopen()}
                </Button>
            ) : null}
            {confirming ? (
                <ReopenDialog
                    orderId={orderId}
                    source={source}
                    onClose={() => setConfirming(false)}
                />
            ) : null}
        </div>
    )
}

function ReopenDialog({
    orderId,
    source,
    onClose,
}: {
    orderId: string
    source: EInvoiceResolutionSource
    onClose: () => void
}) {
    const mutation = useReopenEInvoice()
    const workflow = useWorkflowTracking('einvoice_reopen', {
        attemptKey: orderId,
        operation: 'update',
    })
    const [error, setError] = useState<string>()
    const close = () => {
        workflow.finish('closed')
        onClose()
    }
    const reopen = async () => {
        setError(undefined)
        try {
            await workflow.run(
                () => mutation.mutateAsync({ orderId, source }),
                (invoice) => invoice?.id,
            )
            onClose()
        } catch (failure) {
            setError(resolutionErrorText(failure))
        }
    }
    return (
        <Dialog open onOpenChange={(open) => !open && close()}>
            <DialogContent>
                <DialogHeader>
                    <DialogTitle>{m.einvoice_reopen_title()}</DialogTitle>
                    <DialogDescription>
                        {m.einvoice_reopen_desc()}
                    </DialogDescription>
                </DialogHeader>
                <ResolutionFormError message={error} />
                <DialogFooter>
                    <Button
                        type="button"
                        variant="outline"
                        onClick={close}
                        disabled={mutation.isPending}
                    >
                        {m.einvoice_dialog_cancel()}
                    </Button>
                    <Button
                        type="button"
                        onClick={() => void reopen()}
                        disabled={mutation.isPending}
                    >
                        {m.einvoice_reopen()}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    )
}
